import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { BUTTON_LABELS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { VerticalTabsLayout } from "@mibuildweb/components";
import { useTheme } from "@mui/material";
import { ConfigPageLayout } from "../../../../Config/ConfigPageLayout";
import { IAttributeData } from "../Attributes.types";
import { addAttributesSchema } from "../schema/addAttributes.schema";
import { AttributeDetailsTab } from "../tabs/AttributeDetailsTab/AttributeDetailsTab";
import { useAddAttributesViewModel } from "./useAddAttributesViewModel";

export function AddAttributes() {
  const { params, goBack } = useRouter();
  const { id: attributeId } = params;
  const theme = useTheme();

  const form = useForm<IAttributeData>({
    mode: "onChange",
    defaultValues: {
      name: "",
      allowManualEntry: false,
      inputCharacterType: "Alphanumeric",
      attributeOptions: [{ value: "", abbreviation: "" }],
      categoryIds: [],
    },
    resolver: zodResolver(addAttributesSchema),
  });

  const {
    formState: { isValid, isDirty },
    reset,
    getValues,
    watch,
  } = form;

  const { name } = watch();

  const {
    isLoading,
    fetchAttributeDetails,
    handleCreateAttribute,
    handleUpdateAttribute,
  } = useAddAttributesViewModel({
    goBack,
    reset,
  });

  useEffect(() => {
    if (attributeId) {
      fetchAttributeDetails(attributeId);
    }
  }, []);

  const tabs = useMemo(
    () => [
      {
        label: "Details",
        value: "details",
        controlledFormViewComponent: AttributeDetailsTab,
        isComplete: isValid,
        hasErrors: !isValid,
      },
    ],
    [isValid]
  );

  const handleSubmit = () => {
    const data = getValues();
    if (attributeId) {
      handleUpdateAttribute(data);
    } else {
      handleCreateAttribute(data);
    }
  };

  const renderTitle = () => {
    const action = attributeId ? "Edit" : "Add";
    return (
      <>
        {action} Attribute:{" "}
        <span style={{ color: theme.palette.primary.main }}>{name}</span>
      </>
    );
  };

  const actions = {
    cancel: {
      label: BUTTON_LABELS.CANCEL,
      onClick: () => {
        goBack();
      },
    },
    submit: {
      label: attributeId ? "Update Attribute" : "Save Attribute",
      onClick: () => {
        handleSubmit();
      },
      disabled: !isDirty || !isValid || isLoading,
    },
  };

  return (
    <ConfigPageLayout
      renderTitle={renderTitle}
      bottomActions={actions}
      loading={isLoading}
    >
      <VerticalTabsLayout
        tabs={tabs}
        form={form}
        transparentBackground
        hideNavigationButtons
      />
    </ConfigPageLayout>
  );
}
